import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, MapPin, CheckCircle2, Clock, Truck, Phone, Star, ArrowRightLeft, X, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: 'Awaiting pickup',
  assigned: 'Driver assigned',
  in_progress: 'Out for delivery',
  picked_up: 'Picked up',
  delivered: 'Delivered',
  completed: 'All deliveries complete',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

const PackageTracking = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // Return-parcel state
  const [showReturn, setShowReturn] = useState(false);
  const [returnForm, setReturnForm] = useState({ senderPhone: '', senderName: '', packageDescription: 'Return parcel', packageSize: 'small' });
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnSuccess, setReturnSuccess] = useState(null);
  const [returnError, setReturnError] = useState(null);

  const submitReturn = async () => {
    if (!returnForm.senderPhone) {
      setReturnError('Please enter your phone number.');
      return;
    }
    setReturnLoading(true);
    setReturnError(null);
    try {
      const res = await axios.post(`${API}/api/public/return-parcel/${token}`, returnForm);
      setReturnSuccess(res.data);
    } catch (err) {
      setReturnError(err.response?.data?.detail || 'Could not book return parcel.');
    } finally {
      setReturnLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/api/public/track-package/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to load tracking info');
    }
  };

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 10000);
    return () => clearInterval(intervalRef.current);
  }, [token]); // eslint-disable-line

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="package-track-error">
        <div className="text-center max-w-md">
          <Package className="h-16 w-16 mx-auto text-gray-300 mb-3" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link Unavailable</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  const mapUrl = data.driverLocation?.latitude
    ? `https://maps.google.com/maps?q=${data.driverLocation.latitude},${data.driverLocation.longitude}&z=15&output=embed`
    : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="package-track-page">
      <header className="bg-white border-b px-4 py-3 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <span className="text-pink-600 font-bold text-lg">Tekstar</span>
          <span className="text-xs text-gray-500">Live package tracking</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">From {data.senderName}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-pink-50 text-pink-700 font-medium">
              {STATUS_LABEL[data.status] || data.status}
            </span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-5 w-5 text-pink-600" />
            {data.type === 'multi_drop' ? `Multi-drop · ${data.completedStops}/${data.totalStops} delivered` : 'Package delivery'}
          </h1>
          <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
            <MapPin className="h-4 w-4" /> {data.pickupLocation}
          </p>
        </div>

        {mapUrl && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border">
            <iframe
              src={mapUrl}
              title="Live driver map"
              className="w-full h-72 border-0"
              loading="lazy"
              data-testid="package-track-map"
            />
          </div>
        )}

        {data.driver && (
          <div className="bg-white rounded-xl shadow-sm p-4 border">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Truck className="h-4 w-4 text-pink-600" /> Driver
            </h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base font-semibold">{data.driver.name || 'Driver'}</p>
                <p className="text-sm text-gray-600">{data.driver.vehicle}</p>
                {data.driver.plateNumber && <p className="text-xs text-gray-500 mt-0.5">Plate: {data.driver.plateNumber}</p>}
              </div>
              <div className="flex items-center gap-1 text-sm">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                <span className="font-medium">{(data.driver.rating || 5).toFixed(1)}</span>
              </div>
            </div>
          </div>
        )}

        {data.type === 'multi_drop' && data.stops?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 border" data-testid="package-track-stops">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Delivery stops</h2>
            <div className="space-y-3">
              {data.stops.sort((a, b) => a.order - b.order).map(s => (
                <div key={s.order} className="flex items-start gap-3">
                  <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${s.status === 'delivered' ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {s.status === 'delivered'
                      ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                      : <Clock className="h-4 w-4 text-gray-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Stop {s.order + 1} · {s.receiverName}</p>
                    <p className="text-xs text-gray-600 truncate">{s.location}</p>
                    {s.codAmount > 0 && (
                      <p className="text-xs text-amber-700 mt-0.5">
                        COD K{s.codAmount.toFixed(2)} {s.codCollected ? '✓ collected' : ''}
                      </p>
                    )}
                    {s.deliveredAt && <p className="text-[11px] text-gray-400 mt-0.5">Delivered {new Date(s.deliveredAt).toLocaleString()}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.type === 'single' && (
          <div className="bg-white rounded-xl shadow-sm p-4 border">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Drop-off</h2>
            <p className="text-sm">{data.dropoffLocation}</p>
            {data.receiverName && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Phone className="h-3 w-3" /> Receiver: {data.receiverName}
              </p>
            )}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 pt-4">Powered by Tekstar · Updates every 10s</p>

        {/* Return-parcel CTA */}
        <button
          onClick={() => { setShowReturn(true); setReturnSuccess(null); setReturnError(null); }}
          className="w-full bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white rounded-xl p-4 shadow-md flex items-center gap-3 transition"
          data-testid="return-parcel-btn"
        >
          <ArrowRightLeft className="h-5 w-5 flex-shrink-0" />
          <div className="text-left flex-1">
            <p className="font-semibold text-sm">Need to send something back?</p>
            <p className="text-xs text-pink-100">One-tap return to {data.senderName || 'the sender'} — pre-filled.</p>
          </div>
        </button>
      </div>

      {/* Return-parcel modal */}
      {showReturn && (
        <div
          className="fixed inset-0 bg-black/50 z-20 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !returnLoading && setShowReturn(false)}
          data-testid="return-parcel-modal"
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-base flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-pink-600" /> Send a return parcel
              </h2>
              <button onClick={() => setShowReturn(false)} disabled={returnLoading} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            {returnSuccess ? (
              <div className="p-5 space-y-4 text-center" data-testid="return-parcel-success">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg">Return booked!</h3>
                <div className="bg-gray-50 rounded-lg p-3 text-left text-sm space-y-1">
                  <p><span className="text-gray-500">Pickup:</span> {returnSuccess.pickupLocation}</p>
                  <p><span className="text-gray-500">Drop-off:</span> {returnSuccess.dropoffLocation}</p>
                  <p><span className="text-gray-500">Estimated fare:</span> <span className="font-semibold">K{returnSuccess.estimatedFare?.toFixed(2)}</span></p>
                </div>
                <a
                  href={returnSuccess.trackingUrl}
                  className="block w-full bg-pink-600 hover:bg-pink-700 text-white text-sm font-semibold py-2.5 rounded-md"
                  data-testid="return-parcel-track-link"
                >
                  Track this return
                </a>
                <button onClick={() => setShowReturn(false)} className="text-sm text-gray-500 underline">
                  Close
                </button>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                <div className="bg-pink-50 rounded-lg p-3 text-xs text-pink-900">
                  Pickup at <strong>{data.type === 'multi_drop' ? (data.stops?.[0]?.location || 'your address') : data.dropoffLocation}</strong>, drop-off back to <strong>{data.senderName}</strong>'s pickup point.
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Your phone *</label>
                  <input
                    type="tel"
                    value={returnForm.senderPhone}
                    onChange={(e) => setReturnForm({ ...returnForm, senderPhone: e.target.value })}
                    placeholder="+260 977 123 456"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    data-testid="return-parcel-phone-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Your name (optional)</label>
                  <input
                    type="text"
                    value={returnForm.senderName}
                    onChange={(e) => setReturnForm({ ...returnForm, senderName: e.target.value })}
                    placeholder="John Banda"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    data-testid="return-parcel-name-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Package size</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['small', 'medium', 'large'].map(sz => (
                      <button
                        key={sz}
                        onClick={() => setReturnForm({ ...returnForm, packageSize: sz })}
                        className={`text-xs py-2 rounded-md border font-medium capitalize ${returnForm.packageSize === sz ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'}`}
                        data-testid={`return-parcel-size-${sz}`}
                      >{sz}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={returnForm.packageDescription}
                    onChange={(e) => setReturnForm({ ...returnForm, packageDescription: e.target.value })}
                    placeholder="What's inside?"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                {returnError && (
                  <p className="text-sm text-red-600 bg-red-50 rounded p-2" data-testid="return-parcel-error">{returnError}</p>
                )}

                <button
                  onClick={submitReturn}
                  disabled={returnLoading || !returnForm.senderPhone}
                  className="w-full bg-pink-600 hover:bg-pink-700 disabled:bg-gray-300 text-white font-semibold py-2.5 rounded-md transition"
                  data-testid="return-parcel-submit-btn"
                >
                  {returnLoading ? 'Booking…' : 'Book return parcel'}
                </button>
                <p className="text-[11px] text-gray-400 text-center">No account needed — driver will contact you.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageTracking;
